// src/hooks/useCanvasResize.js
import { useCallback, useRef } from "react";
import { Gradient } from "fabric";

const useCanvasResize = (
  fabricCanvasRef,
  containerRef,
  setSnackbar,
  setCanvasScale,
  showRuler,
  saveHistory,
  getCurrentHistoryState,
  setNoticesMessage,
  backgroundColor,
  setCanvasDimensions
) => {
  const baseScaleRef = useRef(1.0);
  const isInitialResize = useRef(true);

  const updateCanvasSize = useCallback(
    (width, height, initCanvas, zoomLevel) => {
      if (
        !containerRef.current ||
        !initCanvas ||
        !initCanvas.lowerCanvasEl ||
        !initCanvas.lowerCanvasEl.getContext
      ) {
        setSnackbar({
          open: true,
          message: "Canvas not initialized properly.",
          severity: "error",
        });
        console.warn("CanvasResize: updateCanvasSize failed", {
          container: !!containerRef.current,
          canvas: !!initCanvas,
          lowerCanvasEl: !!initCanvas?.lowerCanvasEl,
          getContext: !!initCanvas?.lowerCanvasEl?.getContext,
        });
        return;
      }

      console.log("CanvasResize: Starting resize", {
        width,
        height,
        zoomLevel,
        showRuler,
        backgroundColor,
      });

      try {
        // Calculate new dimensions
        const paddingX = showRuler ? 104 : 64;
        const paddingY = showRuler ? 200 : 120;
        const containerWidth = containerRef.current.offsetWidth - paddingX;
        const containerHeight = containerRef.current.offsetHeight - paddingY;

        console.log("CanvasResize: Container dimensions", {
          containerWidth: containerRef.current.offsetWidth,
          containerHeight: containerRef.current.offsetHeight,
          visibleHeight: containerRef.current.clientHeight,
          viewportHeight: window.innerHeight,
          paddingX,
          paddingY,
          effectiveContainerWidth: containerWidth,
          effectiveContainerHeight: containerHeight,
        });

        if (containerWidth <= 0 || containerHeight <= 0) {
          setSnackbar({
            open: true,
            message: "Invalid container size.",
            severity: "error",
          });
          console.warn("CanvasResize: Invalid container size", {
            containerWidth,
            containerHeight,
          });
          return;
        }

        const aspectRatio = width / height;
        let newWidth = containerWidth;
        let newHeight = newWidth / aspectRatio;

        console.log("CanvasResize: Initial calculated dimensions", {
          newWidth,
          newHeight,
          aspectRatio,
          containerWidth,
          containerHeight,
        });

        if (newHeight > containerHeight) {
          console.log(
            "CanvasResize: Height constrained, adjusting dimensions",
            {
              originalNewWidth: newWidth,
              originalNewHeight: newHeight,
              containerHeight,
            }
          );
          newHeight = containerHeight;
          newWidth = newHeight * aspectRatio;
          console.log("CanvasResize: Adjusted for height constraint", {
            newWidth,
            newHeight,
            adjustedAspectRatio: newWidth / newHeight,
            expectedAspectRatio: aspectRatio,
          });
        }

        newWidth = Math.max(newWidth, 1);
        newHeight = Math.max(newHeight, 1);
        const baseScale = newWidth / width;
        baseScaleRef.current = baseScale;

        const horizontalScale = newWidth / width;
        const verticalScale = newHeight / height;
        const pixelsPerFootHorizontal = horizontalScale * 12;
        const pixelsPerInchVertical = verticalScale;

        console.log("CanvasResize: Final calculated dimensions", {
          newWidth,
          newHeight,
          baseScale,
          aspectRatio,
          pixelAspectRatio: newWidth / newHeight,
          expectedAspectRatio: aspectRatio,
          aspectRatioMatch: Math.abs(newWidth / newHeight - aspectRatio) < 0.01,
          horizontalScale,
          verticalScale,
          scaleMatch: Math.abs(horizontalScale - verticalScale) < 0.01,
          expectedWidth: containerWidth,
          heightConstrained: newHeight === containerHeight,
          widthMatchesExpected: newWidth <= containerWidth,
          pixelsPerFootHorizontal,
          pixelsPerInchVertical,
        });

        // Set canvas dimensions explicitly
        initCanvas.setDimensions({ width: newWidth, height: newHeight });
        setCanvasDimensions({ width: newWidth, height: newHeight });

        // Update history state with new dimensions
        const json = getCurrentHistoryState();
        let offScreenObjects = false;

        if (json && json.objects && json.objects.length > 0) {
          console.log("CanvasResize: Loading history for resize", {
            historyIndex: json.index,
            canvasWidth: json.canvasWidth,
            canvasHeight: json.canvasHeight,
            backgroundColor: json.backgroundColor,
            objects: json.objects.map((obj) => ({
              type: obj.type,
              id: obj.id,
              name: obj.name,
              left: obj.left,
              top: obj.top,
              scaleX: obj.scaleX,
              scaleY: obj.scaleY,
              radius: obj.radius,
              width: obj.width,
              height: obj.height,
              fill: obj.fill,
              stroke: obj.stroke,
              strokeWidth: obj.strokeWidth,
              angle: obj.angle,
              opacity: obj.opacity,
              selectable: obj.selectable,
              evented: obj.evented,
              originX: obj.originX,
              originY: obj.originY,
              flipX: obj.flipX,
              flipY: obj.flipY,
            })),
          });

          const scaleFactorX = json.canvasWidth
            ? newWidth / json.canvasWidth
            : 1;
          const scaleFactorY = json.canvasHeight
            ? newHeight / json.canvasHeight
            : 1;

          // Update JSON dimensions to match the new dimensions
          json.width = newWidth;
          json.height = newHeight;
          json.canvasWidth = newWidth;
          json.canvasHeight = newHeight;

          json.objects = json.objects.filter((obj) => {
            if (obj.type === "circle" && (!obj.radius || obj.radius <= 0)) {
              console.warn("CanvasResize: Skipping invalid circle object", {
                obj,
                reason: "Missing or invalid radius",
              });
              return false;
            }
            if (
              obj.type === "rect" &&
              (!obj.width || !obj.height || obj.width <= 0 || obj.height <= 0)
            ) {
              console.warn("CanvasResize: Skipping invalid rect object", {
                obj,
                reason: "Missing or invalid width/height",
              });
              return false;
            }
            return true;
          });

          if (
            json.backgroundColor &&
            typeof json.backgroundColor === "object"
          ) {
            const coords = json.backgroundColor.coords;
            if (coords) {
              json.backgroundColor.coords = {
                x1: (coords.x1 || 0) * scaleFactorX,
                y1: (coords.y1 || 0) * scaleFactorY,
                x2: (coords.x2 || 0) * scaleFactorX,
                y2: (coords.y2 || 0) * scaleFactorY,
                r1: coords.r1 ? coords.r1 * scaleFactorX : undefined,
                r2: coords.r2 ? coords.r2 * scaleFactorX : undefined,
              };
            }
          }

          const originalObjectProps = json.objects.map((obj) => ({
            id: obj.id,
            scaleX: obj.scaleX || 1,
            scaleY: obj.scaleY || 1,
            width: obj.width,
            height: obj.height,
            radius: obj.radius,
          }));

          initCanvas.loadFromJSON(
            json,
            () => {
              // Reapply dimensions to ensure they are not overridden by JSON
              initCanvas.setDimensions({ width: newWidth, height: newHeight });

              const objects = initCanvas.getObjects();
              objects.forEach((obj, index) => {
                const bounds = obj.getBoundingRect();
                let adjusted = false;

                const originalProps = originalObjectProps[index] || {};

                obj.set({
                  left: (obj.left || 0) * scaleFactorX,
                  top: (obj.top || 0) * scaleFactorY,
                  scaleX: originalProps.scaleX || 1,
                  scaleY: originalProps.scaleY || 1,
                });

                if (obj.type === "rect") {
                  if (originalProps.width && originalProps.height) {
                    obj.set({
                      width: originalProps.width,
                      height: originalProps.height,
                    });
                  }
                } else if (obj.type === "circle" && originalProps.radius) {
                  obj.set({
                    radius: originalProps.radius,
                  });
                }

                const newBounds = obj.getBoundingRect();
                if (
                  newBounds.left < 0 ||
                  newBounds.top < 0 ||
                  newBounds.left + newBounds.width > newWidth ||
                  newBounds.top + newBounds.height > newHeight
                ) {
                  offScreenObjects = true;
                  adjusted = true;
                  obj.set({
                    left: Math.min(
                      Math.max(newBounds.left, 0),
                      newWidth - newBounds.width
                    ),
                    top: Math.min(
                      Math.max(newBounds.top, 0),
                      newHeight - newBounds.height
                    ),
                  });
                }

                obj.setCoords();
                console.log("CanvasResize: Processed object after resize", {
                  type: obj.type,
                  id: obj.id,
                  left: obj.left,
                  top: obj.top,
                  scaleX: obj.scaleX,
                  scaleY: obj.scaleY,
                  radius: obj.radius,
                  width: obj.width,
                  height: obj.height,
                  adjusted,
                  boundsBefore: bounds,
                  boundsAfter: obj.getBoundingRect(),
                  scaleFactorX,
                  scaleFactorY,
                });
              });

              // Apply background color (Fabric.js 6.4.2 syntax)
              try {
                if (
                  typeof backgroundColor === "object" &&
                  backgroundColor.type
                ) {
                  initCanvas.backgroundColor = new Gradient(backgroundColor);
                } else {
                  initCanvas.backgroundColor = backgroundColor;
                }
                initCanvas.requestRenderAll();
                console.log("CanvasResize: Set background color", {
                  backgroundColor,
                  objects: objects.length,
                  width: newWidth,
                  height: newHeight,
                  baseScale,
                  zoomLevel,
                  effectiveScale: baseScale * zoomLevel,
                  offScreenObjects,
                });

                if (offScreenObjects) {
                  setSnackbar({
                    open: true,
                    message:
                      "Some objects were adjusted to fit canvas after resize.",
                    severity: "warning",
                  });
                }

                saveHistory(initCanvas);
              } catch (error) {
                console.error("CanvasResize: Failed to set background color", {
                  error: error.message,
                  backgroundColor,
                  width: newWidth,
                  height: newHeight,
                });
                setSnackbar({
                  open: true,
                  message: "Error setting canvas background color.",
                  severity: "error",
                });
              }
            },
            (obj, error) => {
              console.error("CanvasResize: Error loading object", {
                obj,
                error: error ? error.message : "Unknown error",
              });
              setSnackbar({
                open: true,
                message:
                  "Error restoring canvas objects. Some objects may be missing.",
                severity: "warning",
              });
              initCanvas.requestRenderAll();
            }
          );
        } else {
          // No history, set background color directly
          try {
            initCanvas.backgroundColor = backgroundColor;
            initCanvas.requestRenderAll();
            console.log("CanvasResize: Set background color after resize", {
              backgroundColor,
              width: newWidth,
              height: newHeight,
              baseScale,
              zoomLevel,
              effectiveScale: baseScale * zoomLevel,
            });
            saveHistory(initCanvas);
          } catch (error) {
            console.error("CanvasResize: Failed to set background color", {
              error: error.message,
              backgroundColor,
              width: newWidth,
              height: newHeight,
            });
            setSnackbar({
              open: true,
              message: "Error setting canvas background color.",
              severity: "error",
            });
          }
        }

        setCanvasScale(baseScale * zoomLevel);

        if (!isInitialResize.current) {
          setNoticesMessage(
            "Canvas size changed. Adjust design elements if needed."
          );
        } else {
          setNoticesMessage("");
        }

        isInitialResize.current = false;

        console.log("CanvasResize: Final canvas dimensions", {
          canvasWidth: initCanvas.getWidth(),
          canvasHeight: initCanvas.getHeight(),
          containerWidth: containerRef.current.offsetWidth,
          containerHeight: containerRef.current.offsetHeight,
          visibleHeight: containerRef.current.clientHeight,
          viewportHeight: window.innerHeight,
          rulerPaddingX: paddingX,
          expectedWidth: containerWidth,
          widthMismatch: initCanvas.getWidth() !== containerWidth,
          finalPixelAspectRatio: initCanvas.getWidth() / initCanvas.getHeight(),
          expectedAspectRatio: aspectRatio,
        });
      } catch (error) {
        console.error("CanvasResize: Resize failed", {
          error: error.message,
          width,
          height,
          stack: error.stack,
        });
        setSnackbar({
          open: true,
          message: "Error resizing canvas: " + error.message,
          severity: "error",
        });
      }
    },
    [
      containerRef,
      setSnackbar,
      setCanvasScale,
      showRuler,
      saveHistory,
      getCurrentHistoryState,
      setNoticesMessage,
      backgroundColor,
      setCanvasDimensions,
    ]
  );

  return { updateCanvasSize, baseScaleRef };
};

export default useCanvasResize;
